import { useState, useEffect } from "react";
import { AlertCircle } from "lucide-react";
import { TimeSlot } from "../../types/Availability";
import { Button } from "../../@/components/ui/button";
import { Alert, AlertDescription } from "../../@/components/ui/alert";
import { TimeSelect } from "../../@/components/ui/time-select";
import { parseTime } from "../../lib/utils";

interface TimeSlotPickerProps {
  onSave: (slot: TimeSlot) => void;
  onCancel: () => void;
  hasOverlap?: boolean;
  onTimeChange: (start: string, end: string) => void;
}

export default function TimeSlotPicker({
  onSave,
  onCancel,
  hasOverlap,
  onTimeChange,
}: TimeSlotPickerProps) {
  const [startTime, setStartTime] = useState("09:00 AM");
  const [endTime, setEndTime] = useState("10:00 AM");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (hasOverlap) {
      setError("This time slot overlaps with an existing slot");
    } else {
      setError(null);
    }
  }, [hasOverlap]);

  const handleSave = () => {
    if (startTime && endTime) {
      const startParseTime = parseTime(startTime);
      const endParseTime = parseTime(endTime);
      if (startParseTime >= endParseTime) {
        setError("End time must be after start time");
        return;
      }

      onSave({ start: startTime, end: endTime });
    }
  };

  const handleTimeChange = (
    setter: React.Dispatch<React.SetStateAction<string>>,
    value: string
  ) => {
    setter(value);
    setError(null);
    onTimeChange(startTime, endTime);
  };

  return (
    <div className="space-y-4 bg-muted p-4 rounded-lg">
      {error && (
        <div className={`${error ? "alert-enter" : "alert-exit"}`}>
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        </div>
      )}

      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="grid grid-cols-2 gap-2 w-full sm:w-auto">
          <TimeSelect
            value={startTime}
            onChange={(value) => handleTimeChange(setStartTime, value)}
            className="w-full"
          />
          <TimeSelect
            value={endTime}
            onChange={(value) => handleTimeChange(setEndTime, value)}
            className="w-full"
          />
        </div>

        <div className="flex gap-2 w-full sm:w-auto">
          <Button
            onClick={handleSave}
            // disabled={}
            className="flex-1 sm:flex-none bg-customGreenColor hover:bg-customGreenDark"
          >
            Add
          </Button>
          <Button
            variant="outline"
            onClick={onCancel}
            className="flex-1 sm:flex-none"
          >
            Discard
          </Button>
        </div>
      </div>
    </div>
  );
}
